import React from 'react';
import { Plus, QrCode, Trash2, Smartphone, AlertTriangle } from 'lucide-react';
import {
    apiService,
    messengerApiService,
    instagramApiService,
    MessengerConnectionStatus,
    InstagramConnectionStatus,
    WhatsAppStatus,
} from '../services/api';
import socketService from '../services/socket';

export const WhatsAppConnectionPage: React.FC = () => {
    const [sessions, setSessions] = React.useState<WhatsAppStatus[]>([]);
    const [messengerConnection, setMessengerConnection] = React.useState<MessengerConnectionStatus | null>(null);
    const [instagramConnection, setInstagramConnection] = React.useState<InstagramConnectionStatus | null>(null);
    const [isMessengerLoading, setIsMessengerLoading] = React.useState(false);
    const [isInstagramLoading, setIsInstagramLoading] = React.useState(false);
    const [isMessengerConnecting, setIsMessengerConnecting] = React.useState(false);
    const [isInstagramConnecting, setIsInstagramConnecting] = React.useState(false);
    const [manualMessengerToken, setManualMessengerToken] = React.useState('');
    const [manualMessengerPageId, setManualMessengerPageId] = React.useState('');
    const [manualInstagramToken, setManualInstagramToken] = React.useState('');
    const [manualInstagramPageId, setManualInstagramPageId] = React.useState('');
    const [isManualMessengerConnecting, setIsManualMessengerConnecting] = React.useState(false);
    const [isManualInstagramConnecting, setIsManualInstagramConnecting] = React.useState(false);
    const [selectedSessionId, setSelectedSessionId] = React.useState<string>('default');
    const [refreshTrigger, setRefreshTrigger] = React.useState(0);
    const [deleteModal, setDeleteModal] = React.useState<{show: boolean, sessionId: string | null}>({ show: false, sessionId: null });
    const [warningModal, setWarningModal] = React.useState<{show: boolean, message: string}>({ show: false, message: '' });
    const [isDeleting, setIsDeleting] = React.useState(false);

    const loadSessions = React.useCallback(async () => {
        try {
            const allSessions = await apiService.getAllSessions();
            setSessions(allSessions);
            if (allSessions.length > 0 && !allSessions.find(s => s.sessionId === selectedSessionId)) {
                if (selectedSessionId === 'default') {
                    if (allSessions.length > 0) setSelectedSessionId(allSessions[0].sessionId);
                } else {
                    setSelectedSessionId(allSessions[0].sessionId);
                }
            }
        } catch (e) { console.error(e); }
    }, [selectedSessionId]);

    const loadMessengerConnection = React.useCallback(async () => {
        setIsMessengerLoading(true);
        try {
            const connection = await messengerApiService.getConnection();
            setMessengerConnection(connection);
        } catch (error) {
            console.error('Failed to load Messenger connection:', error);
        } finally {
            setIsMessengerLoading(false);
        }
    }, []);

    const loadInstagramConnection = React.useCallback(async () => {
        setIsInstagramLoading(true);
        try {
            const connection = await instagramApiService.getConnection();
            setInstagramConnection(connection);
        } catch (error) {
            console.error('Failed to load Instagram connection:', error);
        } finally {
            setIsInstagramLoading(false);
        }
    }, []);

    React.useEffect(() => {
        loadSessions();
        loadMessengerConnection();
        loadInstagramConnection();
        const socket = socketService.connect();

        socket.on('whatsapp_status_update', () => loadSessions());
        socket.on('whatsapp_qr', () => loadSessions());
        socket.on('whatsapp_ready', () => loadSessions());
        socket.on('channel_status_update', () => {
            loadMessengerConnection();
            loadInstagramConnection();
        });

        return () => {
            // socket listener cleanup if needed
        };
    }, [refreshTrigger, loadSessions, loadMessengerConnection, loadInstagramConnection]);

    const handleCreateSession = async () => {
        const newId = `session-${Date.now()}`;
        await apiService.createSession(newId);
        setRefreshTrigger(p => p + 1);
        setSelectedSessionId(newId);
    };

    const handleConnectMessenger = async () => {
        try {
            setIsMessengerConnecting(true);
            const { url } = await messengerApiService.getAuthUrl();
            const popup = window.open(url, 'messenger-connect', 'width=600,height=750');

            const onMessage = async (event: MessageEvent) => {
                const payload = event?.data || {};
                if (payload?.type === 'meta_connect_complete') {
                    window.removeEventListener('message', onMessage);
                    await loadMessengerConnection();
                    setIsMessengerConnecting(false);
                }
            };

            window.addEventListener('message', onMessage);
            const timer = window.setInterval(async () => {
                if (!popup || popup.closed) {
                    window.clearInterval(timer);
                    window.removeEventListener('message', onMessage);
                    await loadMessengerConnection();
                    setIsMessengerConnecting(false);
                }
            }, 600);
        } catch (error) {
            console.error('Failed to connect Messenger:', error);
            setIsMessengerConnecting(false);
        }
    };

    const handleConnectInstagram = async () => {
        try {
            setIsInstagramConnecting(true);
            const { url } = await instagramApiService.getAuthUrl();
            const popup = window.open(url, 'instagram-connect', 'width=600,height=750');

            const onMessage = async (event: MessageEvent) => {
                const payload = event?.data || {};
                if (payload?.type === 'instagram_connect_complete') {
                    window.removeEventListener('message', onMessage);
                    await loadInstagramConnection();
                    setIsInstagramConnecting(false);
                }
            };

            window.addEventListener('message', onMessage);
            const timer = window.setInterval(async () => {
                if (!popup || popup.closed) {
                    window.clearInterval(timer);
                    window.removeEventListener('message', onMessage);
                    await loadInstagramConnection();
                    setIsInstagramConnecting(false);
                }
            }, 600);
        } catch (error) {
            console.error('Failed to connect Instagram:', error);
            setIsInstagramConnecting(false);
        }
    };

    const handleDisconnectMessenger = async () => {
        try {
            await messengerApiService.disconnect();
            await loadMessengerConnection();
        } catch (error) {
            console.error('Failed to disconnect Messenger:', error);
        }
    };

    const handleDisconnectInstagram = async () => {
        try {
            await instagramApiService.disconnect();
            await loadInstagramConnection();
        } catch (error) {
            console.error('Failed to disconnect Instagram:', error);
        }
    };

    const handleConnectMessengerWithToken = async () => {
        const trimmedToken = manualMessengerToken.trim();
        const trimmedPageId = manualMessengerPageId.trim();

        if (!trimmedToken) {
            return;
        }

        try {
            setIsManualMessengerConnecting(true);
            await messengerApiService.connectWithToken({
                userAccessToken: trimmedToken,
                pageId: trimmedPageId || undefined,
            });
            await loadMessengerConnection();
            setManualMessengerToken('');
        } catch (error) {
            console.error('Failed to connect Messenger with token:', error);
        } finally {
            setIsManualMessengerConnecting(false);
        }
    };

    const handleConnectInstagramWithToken = async () => {
        const trimmedToken = manualInstagramToken.trim();
        const trimmedPageId = manualInstagramPageId.trim();

        if (!trimmedToken) {
            return;
        }

        try {
            setIsManualInstagramConnecting(true);
            await instagramApiService.connectWithToken({
                accessToken: trimmedToken,
                pageId: trimmedPageId || undefined,
            });
            await loadInstagramConnection();
            setManualInstagramToken('');
        } catch (error) {
            console.error('Failed to connect Instagram with token:', error);
        } finally {
            setIsManualInstagramConnecting(false);
        }
    };

    const handleLogoutSession = async (sessionId: string) => {
        setDeleteModal({ show: true, sessionId });
    };

    const confirmDelete = async () => {
        if (!deleteModal.sessionId) return;
        
        // Prevent deleting the last session
        if (sessions.length <= 1) {
            setWarningModal({
                show: true,
                message: 'You must keep at least one session.'
            });
            setDeleteModal({ show: false, sessionId: null });
            return;
        }
        
        setIsDeleting(true);
        try {
            await apiService.deleteSession(deleteModal.sessionId);
            setRefreshTrigger(p => p + 1);
            if (selectedSessionId === deleteModal.sessionId) {
                // Select another session if available
                const remainingSessions = sessions.filter(s => s.sessionId !== deleteModal.sessionId);
                if (remainingSessions.length > 0) {
                    setSelectedSessionId(remainingSessions[0].sessionId);
                } else {
                    setSelectedSessionId('default');
                }
            }
            setDeleteModal({ show: false, sessionId: null });
        } catch (error) {
            console.error('Error deleting session:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const cancelDelete = () => {
        setDeleteModal({ show: false, sessionId: null });
    };

    const currentSession = sessions.find(s => s.sessionId === selectedSessionId) || { isReady: false, isAuthenticated: false, sessionId: 'default' };

    return (
        <div className="page-wrapper">
            <div className="page-heading">
                <div>
                    <h1>Channels Connection</h1>
                    <p className="subtitle">Manage WhatsApp, Messenger, and Instagram accounts</p>
                </div>
            </div>

            <section className="panel" style={{ marginTop: '1.25rem' }}>
                <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                    <div style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
                        <h3 style={{ marginBottom: '0.5rem' }}>Facebook Messenger</h3>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                            {messengerConnection?.connected ? 'Connected' : 'Not connected'}
                        </p>
                        {messengerConnection?.pageName && (
                            <p style={{ fontSize: '0.9rem' }}>
                                Page: <strong>{messengerConnection.pageName}</strong>
                            </p>
                        )}
                        {messengerConnection?.pageId && (
                            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                Page ID: {messengerConnection.pageId}
                            </p>
                        )}
                    </div>

                    <div style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
                        <h3 style={{ marginBottom: '0.5rem' }}>Instagram</h3>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                            {instagramConnection?.messagingReady
                                ? 'Connected (Messaging Ready)'
                                : instagramConnection?.connectedBasic
                                    ? 'Connected (Basic) - messaging not ready'
                                    : 'Not connected'}
                        </p>
                        {instagramConnection?.igUsername && (
                            <p style={{ fontSize: '0.9rem' }}>
                                Username: <strong>@{instagramConnection.igUsername}</strong>
                            </p>
                        )}
                        {instagramConnection?.igBusinessId && (
                            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                Business ID: {instagramConnection.igBusinessId}
                            </p>
                        )}
                        {instagramConnection?.connectedBasic && !instagramConnection?.messagingReady && (
                            <p style={{ fontSize: '0.8rem', color: 'var(--warning)', marginTop: '0.5rem' }}>
                                Instagram is authenticated, but messaging is not ready. Link Instagram business account to a Facebook page and reconnect.
                            </p>
                        )}
                    </div>

                    <div style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
                        <h3 style={{ marginBottom: '0.5rem' }}>Messenger OAuth</h3>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                            {isMessengerLoading
                                ? 'Loading connection...'
                                : (messengerConnection?.connected ? 'Ready to send/receive' : 'Needs authorization')}
                        </p>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                                className="btn btn-primary"
                                disabled={isMessengerConnecting}
                                onClick={handleConnectMessenger}
                            >
                                {isMessengerConnecting ? 'Connecting...' : 'Connect Messenger'}
                            </button>
                            {messengerConnection?.connected && (
                                <button className="btn btn-secondary" onClick={handleDisconnectMessenger}>
                                    Disconnect Messenger
                                </button>
                            )}
                        </div>
                        <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
                            <textarea
                                value={manualMessengerToken}
                                onChange={(event) => setManualMessengerToken(event.target.value)}
                                rows={3}
                                placeholder="Paste Messenger user/page access token to connect directly"
                                style={{
                                    width: '100%',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius-sm)',
                                    padding: '0.5rem',
                                    fontFamily: 'inherit',
                                    fontSize: '0.9rem',
                                }}
                            />
                            <input
                                type="text"
                                value={manualMessengerPageId}
                                onChange={(event) => setManualMessengerPageId(event.target.value)}
                                placeholder="Optional Page ID (if you manage multiple pages)"
                                style={{
                                    width: '100%',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius-sm)',
                                    padding: '0.5rem',
                                    fontFamily: 'inherit',
                                    fontSize: '0.9rem',
                                }}
                            />
                            <button
                                className="btn btn-secondary"
                                disabled={isManualMessengerConnecting || !manualMessengerToken.trim()}
                                onClick={handleConnectMessengerWithToken}
                            >
                                {isManualMessengerConnecting ? 'Connecting token...' : 'Connect with token'}
                            </button>
                        </div>
                        {messengerConnection?.tokenExpiresAt && (
                            <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                Token expires: {new Date(messengerConnection.tokenExpiresAt).toLocaleString()}
                            </p>
                        )}
                    </div>

                    <div style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
                        <h3 style={{ marginBottom: '0.5rem' }}>Instagram OAuth</h3>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                            {isInstagramLoading
                                ? 'Loading connection...'
                                : (instagramConnection?.messagingReady ? 'Ready to send/receive' : 'Needs authorization')}
                        </p>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                                className="btn btn-primary"
                                disabled={isInstagramConnecting}
                                onClick={handleConnectInstagram}
                            >
                                {isInstagramConnecting ? 'Connecting...' : 'Connect Instagram'}
                            </button>
                            {instagramConnection?.connectedBasic && (
                                <button className="btn btn-secondary" onClick={handleDisconnectInstagram}>
                                    Disconnect Instagram
                                </button>
                            )}
                        </div>
                        <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
                            <textarea
                                value={manualInstagramToken}
                                onChange={(event) => setManualInstagramToken(event.target.value)}
                                rows={3}
                                placeholder="Paste Instagram/Facebook token to connect Instagram directly"
                                style={{
                                    width: '100%',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius-sm)',
                                    padding: '0.5rem',
                                    fontFamily: 'inherit',
                                    fontSize: '0.9rem',
                                }}
                            />
                            <input
                                type="text"
                                value={manualInstagramPageId}
                                onChange={(event) => setManualInstagramPageId(event.target.value)}
                                placeholder="Optional Facebook Page ID for messaging bridge"
                                style={{
                                    width: '100%',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius-sm)',
                                    padding: '0.5rem',
                                    fontFamily: 'inherit',
                                    fontSize: '0.9rem',
                                }}
                            />
                            <button
                                className="btn btn-secondary"
                                disabled={isManualInstagramConnecting || !manualInstagramToken.trim()}
                                onClick={handleConnectInstagramWithToken}
                            >
                                {isManualInstagramConnecting ? 'Connecting token...' : 'Connect with token'}
                            </button>
                        </div>
                        {instagramConnection?.tokenExpiresAt && (
                            <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                Token expires: {new Date(instagramConnection.tokenExpiresAt).toLocaleString()}
                            </p>
                        )}
                    </div>
                </div>
            </section>

            <section className="panel" style={{ marginTop: '2rem' }}>
                <div className="whatsapp-layout-grid">
                    <div className="sessions-sidebar">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <h2 style={{ fontSize: '1.25rem' }}>Sessions</h2>
                            <button 
                                className="btn-icon" 
                                onClick={handleCreateSession} 
                                title="New Session"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'transparent',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    color: 'black',
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'var(--primary-soft)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                }}
                            >
                                <Plus size={20} color="black" />
                            </button>
                        </div>

                        <div className="sessions-list-container">
                            {sessions.length === 0 && <p className="text-secondary" style={{ fontSize: '0.9rem' }}>No sessions created.</p>}
                            {sessions.map((s, index) => (
                                <button
                                    key={s.sessionId}
                                    className={`session-tab ${selectedSessionId === s.sessionId ? 'active' : ''}`}
                                    onClick={() => setSelectedSessionId(s.sessionId)}
                                    title={s.sessionId}
                                    style={{ width: '100%' }}
                                >
                                    <div className="tab-info" style={{ flex: 1 }}>
                                        <span className="tab-name">{s.phoneNumber ? s.phoneNumber : `Session ${index + 1}`}</span>
                                        <span className="tab-id">{s.sessionId.substring(0, 8)}...</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span className={`status-dot ${s.isReady ? 'online' : 'offline'}`}></span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (sessions.length <= 1) {
                                                    setWarningModal({
                                                        show: true,
                                                        message: 'You must keep at least one session.'
                                                    });
                                                    return;
                                                }
                                                setDeleteModal({ show: true, sessionId: s.sessionId });
                                            }}
                                            title={sessions.length <= 1 ? 'Cannot delete the last session' : 'Delete Session'}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                padding: '4px',
                                                cursor: 'pointer',
                                                opacity: 0.6,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.2s ease',
                                                borderRadius: '4px'
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.background = 'var(--danger-soft)';
                                                e.currentTarget.style.opacity = '1';
                                                e.currentTarget.style.transform = 'scale(1.1)';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.background = 'transparent';
                                                e.currentTarget.style.opacity = '0.6';
                                                e.currentTarget.style.transform = 'scale(1)';
                                            }}
                                        >
                                            <Trash2 size={14} color="var(--danger)" />
                                        </button>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="session-detail-view">
                        {!currentSession.isReady && currentSession.qrCode ? (
                            <div className="qr-block">
                                <h3 style={{ marginBottom: '1rem' }}>Scan QR Code</h3>
                                <img src={currentSession.qrCode} alt="WhatsApp QR Code" />
                                <div style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
                                    <p>1. Open WhatsApp on your phone</p>
                                    <p>2. Go to Settings {'>'} Linked Devices</p>
                                    <p>3. Tap on Link a Device</p>
                                    <p>4. Point your phone to this screen</p>
                                </div>
                                <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Session ID: {currentSession.sessionId}</p>
                            </div>
                        ) : (
                            <div className="empty-state" style={{ background: 'transparent', boxShadow: 'none', border: 'none' }}>
                                {currentSession.isReady ? (
                                    <>
                                        <div style={{
                                            width: '80px', height: '80px', background: 'rgba(5, 150, 105, 0.1)',
                                            borderRadius: '50%', display: 'grid', placeItems: 'center', margin: '0 auto 1.5rem auto'
                                        }}>
                                            <Smartphone size={40} color="var(--success)" />
                                        </div>
                                        <h2 style={{ marginBottom: '0.5rem' }}>Connected</h2>
                                        <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                                            Session is active and ready to send messages.
                                        </p>
                                        <div style={{ background: 'var(--surface-muted)', padding: '1rem', borderRadius: 'var(--radius-md)', marginBottom: '2rem' }}>
                                            <p style={{ fontWeight: 600 }}>{currentSession.phoneNumber}</p>
                                            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{currentSession.sessionId}</p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div style={{
                                            width: '80px', height: '80px', background: 'var(--surface-muted)',
                                            borderRadius: '50%', display: 'grid', placeItems: 'center', margin: '0 auto 1.5rem auto'
                                        }}>
                                            <QrCode size={40} color="var(--text-secondary)" />
                                        </div>
                                        {sessions.length > 0 ? (
                                            <p>Select a session to view details or QR code.</p>
                                        ) : (
                                            <p>Create a new session to get started.</p>
                                        )}
                                    </>
                                )}

                                {currentSession.sessionId !== 'default' && ( /* Prevent deleting default session just in case, or allow it */
                                    <button 
                                    className="btn btn-secondary" 
                                    onClick={() => handleLogoutSession(currentSession.sessionId)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <Trash2 size={16} />
                                    Logout Session
                                </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Delete Confirmation Modal */}
            {deleteModal.show && (
                <div className="modal-overlay" onClick={cancelDelete}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px', padding: '24px' }}>
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <div style={{
                                display: 'inline-flex',
                                background: 'var(--danger-soft)',
                                borderRadius: '50%',
                                padding: '12px',
                                marginBottom: '16px'
                            }}>
                                <AlertTriangle size={32} color="var(--danger)" />
                            </div>
                            <h3 style={{ margin: '0 0 8px' }}>Delete Session</h3>
                            <p style={{ color: 'var(--text-secondary)', margin: '0 0 24px' }}>
                                Are you sure you want to delete this session? This action cannot be undone.
                            </p>
                        </div>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px',
                            paddingTop: '16px',
                            borderTop: '1px solid var(--border)'
                        }}>
                            <button 
                                className="btn btn-secondary" 
                                onClick={cancelDelete}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-danger" 
                                onClick={confirmDelete}
                                disabled={isDeleting}
                                style={{
                                    background: 'var(--danger)',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    opacity: isDeleting ? 0.7 : 1,
                                    cursor: isDeleting ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isDeleting ? (
                                    <>
                                        <span className="spinner" style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid rgba(255,255,255,0.3)',
                                            borderTopColor: 'white',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite',
                                            display: 'inline-block'
                                        }}></span>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} />
                                        Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* @ts-ignore - styled-jsx types */}
            {/* Warning Modal */}
            {warningModal.show && (
                <div className="modal-overlay" onClick={() => setWarningModal({...warningModal, show: false})}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px', padding: '24px' }}>
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <div style={{
                                display: 'inline-flex',
                                background: 'var(--warning-soft)',
                                borderRadius: '50%',
                                padding: '12px',
                                marginBottom: '16px'
                            }}>
                                <AlertTriangle size={32} color="var(--warning)" />
                            </div>
                            <h3 style={{ margin: '0 0 8px' }}>Warning</h3>
                            <p style={{ color: 'var(--text-secondary)', margin: '0 0 24px' }}>
                                {warningModal.message}
                            </p>
                            <button 
                                className="btn btn-primary"
                                onClick={() => setWarningModal({...warningModal, show: false})}
                                style={{
                                    padding: '8px 24px',
                                    borderRadius: '6px',
                                    background: 'var(--primary)',
                                    color: 'black',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'background 0.2s ease',
                                    fontWeight: 'bold'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'var(--primary-dark)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'var(--primary)';
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* @ts-ignore - styled-jsx types */}
            <style jsx global>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                
                .btn-danger:hover {
                    background: var(--danger-dark) !important;
                }
                
                .btn-icon {
                    background: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                }
                
                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }

                .modal-content {
                    background: var(--surface);
                    border-radius: 12px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
                    width: 90%;
                    max-width: 500px;
                    animation: modalFadeIn 0.3s ease;
                }

                @keyframes modalFadeIn {
                    from { opacity: 0; transform: translateY(-20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};
